'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import type { LanguageVariablesApiResponse } from '@/types/language';

interface LanguageVariablesContextValue {
  variables: Record<string, string>;
  getVariable: (key: string, fallback: string) => string;
  isLoading: boolean;
  error: string | null;
  language: string | undefined;
  refetch: () => Promise<void>;
}

const LanguageVariablesContext = createContext<LanguageVariablesContextValue | undefined>(undefined);

interface LanguageVariablesProviderProps {
  children: React.ReactNode;
  initialLanguage?: string;
  initialVariables?: Record<string, string>; // Server-side'da çekilen translations (client-side fetch'i önler)
}

// Cache için global store (component mount/unmount'larından bağımsız)
const cache = new Map<string, { data: Record<string, string>; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika
const pendingRequests = new Map<string, Promise<Record<string, string>>>();

export function LanguageVariablesProvider({ children, initialLanguage, initialVariables }: LanguageVariablesProviderProps) {
  // Server-side'da çekilen translations varsa direkt kullan, yoksa boş object ile başla
  const pathname = usePathname();
  const [variables, setVariables] = useState<Record<string, string>>(initialVariables || {});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<string | undefined>(initialLanguage);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Dil kodunu belirle
  const detectLanguage = useCallback((): string | undefined => {
    // 1. Props'tan gelen dil
    if (initialLanguage) {
      return initialLanguage;
    }

    // 2. Client-side'da pathname'den
    if (typeof window !== 'undefined') {
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const possibleLang = pathParts[0];
      if (possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang)) {
        return possibleLang;
      }

      // 3. Cookie'den
      const cookieLang = document.cookie
        .split('; ')
        .find(row => row.startsWith('language='))
        ?.split('=')[1];
      if (cookieLang) {
        return cookieLang;
      }
    }

    return undefined;
  }, [initialLanguage]);

  // Variables fetch fonksiyonu
  const fetchVariables = useCallback(async (language: string, signal?: AbortSignal, forceRefresh = false): Promise<Record<string, string>> => {
    // Cache kontrolü (forceRefresh ise atla)
    if (!forceRefresh) {
      const cached = cache.get(language);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.data;
      }
    }

    // Eğer aynı dil için zaten bir istek varsa, onu bekle
    const pending = pendingRequests.get(language);
    if (pending && !forceRefresh) {
      return pending;
    }

    // Yeni istek oluştur
    const requestPromise = (async () => {
      try {
        // Cache bypass için timestamp ekle
        const url = `/api/language/variables?language=${encodeURIComponent(language)}${forceRefresh ? `&_t=${Date.now()}` : ''}`;
        const response = await fetch(url, {
          signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch variables: ${response.status}`);
        }

        const rawResponse = await response.json();
        const data: LanguageVariablesApiResponse = rawResponse;
        

        
        if (data.success && data.data?.variables) {
          // Cache'e kaydet
          const variables = data.data.variables;
    
          
          cache.set(language, {
            data: variables,
            timestamp: Date.now(),
          });
          
          return variables;
        }

        return {};
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          throw err;
        }
        return {};
      } finally {
        // İstek tamamlandığında pending'den kaldır
        pendingRequests.delete(language);
      }
    })();

    pendingRequests.set(language, requestPromise);
    return requestPromise;
  }, []);

  // Variables'ı yükle
  const loadVariables = useCallback(async (skipIfInitialized = false) => {
    const language = detectLanguage();
    if (!language) {
      setVariables({});
      setCurrentLanguage(undefined);
      setIsLoading(false);
      return;
    }

    // Dil değişmediyse ve zaten yüklüyse, tekrar yükleme
    if (currentLanguage === language) {
      return;
    }

    // Eğer initialVariables varsa ve dil eşleşiyorsa, tekrar fetch yapma
    if (skipIfInitialized && initialVariables && Object.keys(initialVariables).length > 0 && language === initialLanguage) {
      setVariables(initialVariables);
      setCurrentLanguage(language);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Önceki isteği iptal et
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const vars = await fetchVariables(language, abortControllerRef.current.signal, false);
      setVariables(vars);
      setCurrentLanguage(language);
      

    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // İptal edildi, hata gösterme
      }
      setError(err instanceof Error ? err.message : 'Failed to load variables');
      console.error('[LanguageVariablesContext] Load error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [detectLanguage, fetchVariables, currentLanguage, initialVariables, initialLanguage]);

  // İlk yükleme - initialVariables varsa skipIfInitialized=true ile çağır
  useEffect(() => {
    // Eğer initialVariables varsa ve dil eşleşiyorsa direkt kullan, fetch yapma
    const language = detectLanguage();
    if (initialVariables && Object.keys(initialVariables).length > 0 && language === initialLanguage) {
      setVariables(initialVariables);
      setCurrentLanguage(language);
      setIsLoading(false);
      return;
    }
    
    // Aksi halde fetch yap
    loadVariables(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dil değişikliklerini dinle (cookie veya pathname değiştiğinde)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const checkLanguageChange = () => {
      const newLanguage = detectLanguage();
      if (newLanguage !== currentLanguage) {
        loadVariables();
      }
    };

    // Observe Next navigation without replacing the router's history methods.
    checkLanguageChange();

    // Popstate event'i (geri/ileri butonları)
    window.addEventListener('popstate', checkLanguageChange);

    // Cookie değişikliklerini dinle (polling - daha iyi bir yöntem yok)
    // Interval'ı 5 saniyeye çıkarıyoruz (performans için)
    const cookieCheckInterval = setInterval(checkLanguageChange, 5000);

    return () => {
      window.removeEventListener('popstate', checkLanguageChange);
      clearInterval(cookieCheckInterval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [pathname, currentLanguage, detectLanguage, loadVariables]);

  // getVariable helper
  const getVariable = useCallback(
    (key: string, fallback: string): string => {
      const value = variables[key];
      
      // Debug: Eğer key bulunamazsa ve cookie_consent key'i ise detaylı logla (sadece development'ta)

      
      return value || fallback;
    },
    [variables, currentLanguage]
  );

  // Manuel refetch
  const refetch = useCallback(async () => {
    const language = detectLanguage();
    if (!language) {
      return;
    }

    // Cache'i temizle ve force refresh yap
    cache.delete(language);
    pendingRequests.delete(language);
    
    setIsLoading(true);
    setError(null);
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      const vars = await fetchVariables(language, abortControllerRef.current.signal, true);
      setVariables(vars);
      setCurrentLanguage(language);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load variables');
      console.error('[LanguageVariablesContext] Refetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [detectLanguage, fetchVariables]);

  const value: LanguageVariablesContextValue = {
    variables,
    getVariable,
    isLoading,
    error,
    language: currentLanguage,
    refetch,
  };

  return <LanguageVariablesContext.Provider value={value}>{children}</LanguageVariablesContext.Provider>;
}

// Hook
export function useLanguageVariables() {
  const context = useContext(LanguageVariablesContext);

  // GrapesJS / render-component gibi Next uygulaması dışındaki
  // server-side render ortamlarda provider sarmalanmıyor.
  // Bu durumlarda güvenli bir fallback ile sadece verilen
  // fallback metinlerini kullanıyoruz.
  if (context === undefined) {
    const isServerSide = typeof window === "undefined";
    const isGrapesPreview =
      typeof globalThis !== "undefined" &&
      (globalThis as typeof globalThis & { __GRAPES_PREVIEW__?: boolean })
        .__GRAPES_PREVIEW__ === true;

    if (isServerSide || isGrapesPreview) {
      const fallback: LanguageVariablesContextValue = {
        variables: {},
        getVariable: (key: string, fallbackValue: string) => fallbackValue,
        isLoading: false,
        error: null,
        language: undefined,
        refetch: async () => {
          // no-op – GrapesJS önizlemesinde istek atmak istemiyoruz
        },
      };

      return fallback;
    }

    // Gerçek frontend tarafında provider olmadan kullanılırsa
    // hâlâ net bir hata verelim.
    throw new Error(
      "useLanguageVariables must be used within a LanguageVariablesProvider"
    );
  }

  return context;
}

// Sadece getVariable için basit hook (provider olmadan da çalışabilir, fallback kullanır)
export function useGetVariable() {
  const context = useContext(LanguageVariablesContext);
  return useCallback(
    (key: string, fallback: string): string => {
      return context?.variables[key] || fallback;
    },
    [context?.variables]
  );
}

